/**
 * Directions of a train stop signal image: the signal image is only required when the
 * train stops at the towards station later on the trip, optionally only for one train series.
 *
 * Form values: [{ stopCode, stationName, trainSeries }] where trainSeries is a string ("" = all series).
 * API: [{ towardsStopCode, towardsStationName, trainSeries }] where trainSeries 0 = all series.
 */

export const ALL_TRAIN_SERIES = 0;

const messages = {
  stationRequired: {
    id: "modules.TrainStopSignalImages.directions.stationRequired",
    description: "Error when a direction has no station",
    defaultMessage: "Choose a station",
  },
  trainSeriesInvalid: {
    id: "modules.TrainStopSignalImages.directions.trainSeriesInvalid",
    description: "Error when the train series is not a whole hundred",
    defaultMessage: "Use a whole hundred, e.g. 18900",
  },
  duplicate: {
    id: "modules.TrainStopSignalImages.directions.duplicate",
    description: "Error when a direction occurs twice",
    defaultMessage: "This direction already exists",
  },
};

export const emptyDirection = () => ({
  stopCode: "",
  stationName: "",
  trainSeries: "",
});

export const fromApiDirections = (directions = []) =>
  directions.map((direction) => ({
    stopCode: direction.towardsStopCode,
    stationName: direction.towardsStationName,
    trainSeries: direction.trainSeries ? String(direction.trainSeries) : "",
  }));

export const toApiDirections = (directions = []) =>
  directions.map(({ stopCode, stationName, trainSeries }) => ({
    stopCode,
    stationName,
    trainSeries: trainSeries === "" ? ALL_TRAIN_SERIES : Number(trainSeries),
  }));

const isValidTrainSeries = (trainSeries) => {
  if (trainSeries === "") return true;
  const number = Number(trainSeries);
  return Number.isInteger(number) && number > 0 && number % 100 === 0;
};

/**
 * Returns an array with an error object per direction, or undefined when all are valid.
 */
export const validateDirections = (directions = [], intl) => {
  const seen = new Set();
  let hasErrors = false;

  const errors = directions.map(({ stopCode, trainSeries }) => {
    const error = {};

    if (!stopCode) {
      error.stopCode = intl.formatMessage(messages.stationRequired);
    }
    if (!isValidTrainSeries(trainSeries)) {
      error.trainSeries = intl.formatMessage(messages.trainSeriesInvalid);
    }

    const key = `${stopCode}|${Number(trainSeries || ALL_TRAIN_SERIES)}`;
    if (stopCode && seen.has(key)) {
      error.stopCode = intl.formatMessage(messages.duplicate);
    }
    seen.add(key);

    if (Object.keys(error).length > 0) hasErrors = true;
    return error;
  });

  return hasErrors ? errors : undefined;
};

/**
 * Label of an API direction, e.g. "Valkenburg (18900)" or "Sittard" for all series.
 */
export const formatDirection = ({ towardsStationName, trainSeries }) =>
  trainSeries ? `${towardsStationName} (${trainSeries})` : towardsStationName;
